"use client"

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useState,
} from "react"
import { useLatestRef } from "@/hooks/useLatestRef"

interface PaneBackEntry {
    id: string
    depth: number
    label?: string
    title?: string
    onBack: () => void
}

interface PaneBackRegistry {
    register: (entry: PaneBackEntry) => () => void
}

export const PaneBackContext = createContext<PaneBackRegistry | null>(null)

export const PaneDepthContext = createContext(0)

export const PaneShownContext = createContext(true)

export function deepestOf(entries: PaneBackEntry[]): PaneBackEntry | undefined {
    return entries.reduce<PaneBackEntry | undefined>(
        (deepest, entry) =>
            !deepest || entry.depth >= deepest.depth ? entry : deepest,
        undefined
    )
}

export function usePaneBackStack() {
    const [entries, setEntries] = useState<PaneBackEntry[]>([])
    const register = useCallback((entry: PaneBackEntry) => {
        setEntries(current => [
            ...current.filter(existing => existing.id !== entry.id),
            entry,
        ])
        return () =>
            setEntries(current =>
                current.filter(existing => existing.id !== entry.id)
            )
    }, [])
    const registry = useMemo(() => ({ register }), [register])
    const openPane = deepestOf(entries)
    const closeOpenPane = useCallback((): boolean => {
        if (!openPane) return false
        openPane.onBack()
        return true
    }, [openPane])
    return { registry, openPane, closeOpenPane }
}

export function usePaneBack({
    isOpen,
    label,
    title,
    onBack,
}: {
    isOpen: boolean
    label?: string
    title?: string
    onBack: () => void
}) {
    const registry = useContext(PaneBackContext)
    const depth = useContext(PaneDepthContext)
    const id = useId()
    const onBackRef = useLatestRef(onBack)
    const callBack = useCallback(() => onBackRef.current(), [onBackRef])

    useEffect(() => {
        if (!isOpen || !registry) return
        return registry.register({ id, depth, label, title, onBack: callBack })
    }, [isOpen, registry, id, depth, label, title, callBack])

    return { hasRegistry: registry !== null }
}
